/**
 * Role capability helpers — keep in sync with books/permissions.py
 */

import type { UserRole } from "./registry";
import type { MySelf } from "./utils";

export const canEditSeries = (role?: UserRole): role is "owner" | "manager" =>
  role === "owner" || role === "manager";

export const canDeleteSeries = (role?: UserRole): role is "owner" =>
  role === "owner";

export const canManageSharing = (
  role?: UserRole,
): role is "owner" | "manager" => role === "owner" || role === "manager";

export const canManageManagers = (role?: UserRole): role is "owner" =>
  role === "owner";

export const canEditEpisode = (role?: UserRole): role is "owner" | "manager" =>
  role === "owner" || role === "manager";

export const canDeleteEpisode = (
  role?: UserRole,
): role is "owner" | "manager" => role === "owner" || role === "manager";

export const canOpenEditor = (
  role?: UserRole,
): role is "owner" | "manager" | "writer" =>
  role === "owner" || role === "manager" || role === "writer";

export const canValidateEpisode = (
  role?: UserRole,
): role is "owner" | "manager" | "writer" =>
  role === "owner" || role === "manager" || role === "writer";

export const canAddEpisode = (role?: UserRole): role is "owner" | "manager" =>
  role === "owner" || role === "manager";

// Keep in sync with books/permissions.py::can_post_produce
export const canPostProduce = (role?: UserRole): role is "owner" | "manager" =>
  role === "owner" || role === "manager";

// Voices-only post-production: the full-access roles, plus voice actors. A superset of
// canPostProduce, so callers gate on this and then use canPostProduce to tell full access
// from voices-only.
// Keep in sync with books/permissions.py::can_post_produce_voices
export const canPostProduceVoices = (
  role?: UserRole,
): role is "owner" | "manager" | "voice_actor" =>
  role === "owner" || role === "manager" || role === "voice_actor";

// Same roles as post-production today; kept as its own helper so the two can diverge.
// Keep in sync with books/permissions.py::can_publish
export const canPublish = (role?: UserRole): role is "owner" | "manager" =>
  role === "owner" || role === "manager";

// Synthetic voices spend engine credits on every build that meets a missing recording, so they
// are an internal-tier tool on top of post-production access. Superusers keep the admin path.
// Keep in sync with books/permissions.py::can_configure_synth
export const canConfigureSynth = (
  user: Pick<MySelf, "isSuperuser" | "userTier">,
  role?: UserRole,
): boolean =>
  user.isSuperuser || (user.userTier === "internal" && canPostProduce(role));
